"use client";

import { useEffect } from "react";

declare global {
  interface Window {
    __webVitalsBound?: boolean;
  }
}

interface Props {
  /** Welk fractie van metingen ook naar /api/web-vitals POSTen voor het
   *  dashboard. 0 = uit, 0.1 = 10% (default), 1 = alle. Lager = minder
   *  rows in Supabase. dataLayer/GTM-route blijft 100%. */
  sampleRate?: number;
  /** Path naar de POST endpoint. Default `/api/web-vitals` (same-origin).
   *  Alleen wijzigen als je naar een centraal dashboard-domein wilt POSTen. */
  endpoint?: string;
}

/**
 * WebVitalsTracker — push Core Web Vitals naar de GTM dataLayer + (gesampled)
 * naar Supabase voor het /insights/web-vitals dashboard.
 *
 * Meet bij elke pagina-load:
 *   • LCP — Largest Contentful Paint (laad-snelheid hoofdcontent)
 *   • CLS — Cumulative Layout Shift (visuele stabiliteit)
 *   • INP — Interaction to Next Paint (klik-responsiviteit)
 *
 * Plus extra signalen (alleen naar dataLayer, niet naar Supabase):
 *   • FCP — First Contentful Paint
 *   • TTFB — Time to First Byte
 *
 * Stuurt via dataLayer.push naar GTM zodat ze in GA4 als events landen.
 * Plus: 10% sampling naar /api/web-vitals voor het dashboard (LCP/CLS/INP).
 *
 * Idempotent — guard flag voorkomt dubbel-binden bij Fast Refresh.
 */
export default function WebVitalsTracker({
  sampleRate = 0.1,
  endpoint = "/api/web-vitals",
}: Props = {}) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.__webVitalsBound) return;
    window.__webVitalsBound = true;

    // Sample-decision per page-load. Eén beslissing per sessie zodat ALLE
    // metrics van die ene pageview meegaan, of geen enkele — anders zien
    // we per page een mix waar LCP wel maar INP niet meeging.
    const sampled = Math.random() < sampleRate;

    void import("web-vitals").then(({ onCLS, onFCP, onINP, onLCP, onTTFB }) => {
      const dl: Array<Record<string, unknown>> = (window.dataLayer ??= []);

      const push = (m: {
        name: string;
        value: number;
        rating: string;
        navigationType?: string;
      }) => {
        // Round naar hele getallen voor LCP/INP/TTFB/FCP (ms).
        // CLS is een ratio (typisch 0.0–0.5), bewaar 3 decimalen.
        const rounded =
          m.name === "CLS" ? Math.round(m.value * 1000) / 1000 : Math.round(m.value);

        const payload = {
          event: "web_vitals" as const,
          name: m.name,
          value: rounded,
          rating: m.rating,
          navigation_type: m.navigationType ?? "navigate",
          page_path: window.location.pathname,
        };
        dl.push(payload);

        // Sampled POST naar dashboard — alleen LCP/CLS/INP (de 3 Core
        // Web Vitals die Google's ranking gebruikt). FCP/TTFB worden wel
        // naar dataLayer gepusht voor GA4 maar niet hier opgeslagen.
        if (
          sampled &&
          (m.name === "LCP" || m.name === "CLS" || m.name === "INP") &&
          typeof navigator !== "undefined"
        ) {
          const body = JSON.stringify({
            metric: m.name,
            value: rounded,
            rating: m.rating,
            page_path: payload.page_path,
            navigation_type: payload.navigation_type,
          });
          // sendBeacon = fire-and-forget, blijft staan bij page-unload.
          // Fallback naar fetch (keepalive) als sendBeacon ontbreekt.
          try {
            if (navigator.sendBeacon) {
              navigator.sendBeacon(endpoint, new Blob([body], { type: "application/json" }));
            } else {
              void fetch(endpoint, {
                method: "POST",
                body,
                headers: { "Content-Type": "application/json" },
                keepalive: true,
              });
            }
          } catch {
            /* never block UX on telemetry */
          }
        }
      };
      onLCP(push);
      onCLS(push);
      onINP(push);
      onFCP(push);
      onTTFB(push);
    });
  }, [sampleRate, endpoint]);

  return null;
}
